import {
  API_V1_PREFIX,
  apiErrorSchema,
  avatarPath,
  type Attachment,
  type Message,
} from '@orbit-sync/shared-types';
import { getAccessToken } from '../tokenStorage.js';
import { API_BASE_URL, refreshSession } from './client.js';
import { ApiRequestError, NetworkError } from './errors.js';

/**
 * Shared files.
 *
 * Kept out of `request()`: an upload needs progress events, which only
 * `XMLHttpRequest` reports, and a download is a binary body, not JSON. The token
 * policy is the same - attached as a bearer header, refreshed on a 401.
 */

export interface UploadHandle {
  done: Promise<Message>;
  cancel: () => void;
}

/**
 * Sends a file to a channel. `onProgress` gets 0..1 as the body goes out.
 *
 * A 401 refreshes the session and sends the file again, once: an access token
 * that ran out mid-afternoon must not cost the person their upload.
 */
export function uploadFile(
  channelId: string,
  file: File,
  onProgress: (fraction: number) => void,
): UploadHandle {
  let xhr: XMLHttpRequest | null = null;
  let cancelled = false;

  const attempt = (retried: boolean): Promise<Message> =>
    new Promise<Message>((resolve, reject) => {
      const request = new XMLHttpRequest();
      xhr = request;
      request.open('POST', `${API_BASE_URL}${API_V1_PREFIX}/files?channelId=${channelId}`);
      request.withCredentials = true;

      const token = getAccessToken();

      if (token) {
        request.setRequestHeader('authorization', `Bearer ${token}`);
      }

      // Always octet-stream, so no body parser on the server can mistake a JSON
      // or text file for a request body. The real type rides in its own header.
      request.setRequestHeader('content-type', 'application/octet-stream');
      request.setRequestHeader('x-file-name', encodeURIComponent(file.name));
      request.setRequestHeader('x-file-type', file.type || 'application/octet-stream');
      request.responseType = 'json';

      request.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) {
          onProgress(event.loaded / event.total);
        }
      };

      request.onload = () => {
        if (request.status >= 200 && request.status < 300) {
          resolve(request.response as Message);
          return;
        }

        if (request.status === 401 && token && !retried && !cancelled) {
          void refreshSession().then((outcome) => {
            if (outcome.status === 'ok' && !cancelled) {
              onProgress(0);
              attempt(true).then(resolve, reject);
            } else {
              reject(uploadError(request));
            }
          });
          return;
        }

        reject(uploadError(request));
      };

      request.onerror = () => reject(new NetworkError(new Error('Upload failed')));
      request.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));

      request.send(file);
    });

  return {
    done: attempt(false),
    cancel: () => {
      cancelled = true;
      xhr?.abort();
    },
  };
}

function uploadError(request: XMLHttpRequest): ApiRequestError {
  const parsed = apiErrorSchema.safeParse(request.response);

  return new ApiRequestError(
    request.status,
    parsed.success
      ? parsed.data
      : {
          error: {
            code: 'validation_failed',
            message:
              request.status === 413
                ? 'That file is too large'
                : `Upload failed (${request.status})`,
          },
        },
  );
}

/**
 * Downloads a shared file and hands it to the browser's save flow.
 *
 * Fetched rather than linked: the endpoint needs the bearer token, which a plain
 * `<a href>` cannot send.
 */
export async function downloadAttachment(attachment: Attachment): Promise<void> {
  const fetchFile = async (): Promise<Response> => {
    const token = getAccessToken();

    try {
      return await fetch(`${API_BASE_URL}${API_V1_PREFIX}/files/${attachment.fileId}`, {
        headers: token ? { authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
    } catch (error) {
      throw new NetworkError(error);
    }
  };

  let response = await fetchFile();

  // The access token ran out: refresh the session and ask once more.
  if (response.status === 401 && (await refreshSession()).status === 'ok') {
    response = await fetchFile();
  }

  if (!response.ok) {
    throw new ApiRequestError(response.status, {
      error: {
        code: response.status === 410 ? 'not_found' : 'internal_error',
        message:
          response.status === 410
            ? 'That file is no longer available'
            : `Download failed (${response.status})`,
      },
    });
  }

  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = attachment.name;
  document.body.append(link);
  link.click();
  link.remove();
  // Revoked on the next turn: some browsers start the save asynchronously.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Where a user's picture is served from, or `null` when they have none. */
export function avatarUrl(user: { id: string; avatarUpdatedAt: string | null }): string | null {
  const path = avatarPath(user);
  return path ? `${API_BASE_URL}${API_V1_PREFIX}${path}` : null;
}
